import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core'
import { isActionItem, isAnnotation, isHighlight, isScreenShare } from 'src/bookmarks/types'
import { INote, INoteClip } from 'src/call/types'
import { classes } from 'src/utils/css'

import { CallStore, copyShareUrl, getNoteClipShareDisabledReason, NotesStore } from '../state'
import { ActionIcon, clipDimensionalStyle, playClip } from './ui'

@Component({
  tag: 'call-detail-notes-note-clip',
})
export class NoteClip {
  @Prop() clip: INoteClip
  @Prop() readonly: boolean
  @Prop() showMenu: boolean
  @Prop() highlit: boolean
  @Event({ bubbles: false }) delete: EventEmitter<INote>
  @Event({ bubbles: false }) edit: EventEmitter<INote>

  @State() hover = false
  inputEl: HTMLCallDetailNotesNoteInputElement

  render() {
    if (!this.clip) {
      return
    }

    const buttonTheme = 'text-white text-opacity-20 hover:text-opacity-100 hover:bg-white hover:bg-opacity-20'

    const allowShare = isAnnotation(this.clip.note.bookmark) || isScreenShare(this.clip.note.bookmark)
    const noteShareDisabledReason = getNoteClipShareDisabledReason(this.clip)
    const smallText = isActionItem(this.clip.note.bookmark) || isScreenShare(this.clip.note.bookmark)

    return (
      <Host
        class='group flex flex-row p-1 max-w-full text-base font-medium hover:bg-white hover:bg-opacity-10 rounded-md cursor-pointer spacing-x-2px'
        onClick={async () => {
          if (!(await this.inputEl.isFocused())) {
            playClip(this.clip.start_time, 'side-panel-note', this.clip.note.id)
          }
        }}
        onMouseEnter={() => (this.hover = true)}
        onMouseLeave={() => (this.hover = false)}
      >
        <call-detail-notes-note-input
          ref={(el) => (this.inputEl = el)}
          class={`
            overflow-hidden leading-1em w-full px-3px py-2px
            ${classes(smallText, 'text-sm')}
          `}
          style={clipDimensionalStyle}
          canEdit={!this.readonly}
          key={`call-detail-notes-note-input-${this.clip.id}`}
          onEdited={({ detail }) => this.edit.emit({ ...this.clip.note, text: detail.text })}
          note={this.clip.note}
          startTime={CallStore.state.call?.recording?.started_at}
          summarySize='xs'
          timestampSize='xs'
          hoverIcon={this.hover ? 'play' : null}
        />

        {allowShare && (
          <ActionIcon
            icon='link-45deg'
            hoverTip={noteShareDisabledReason || 'Copy Share Link'}
            onClick={async (e) => {
              // Block propagation to playClip
              e.stopPropagation()
              await copyShareUrl(this.clip)
            }}
            iconClass='text-xl'
            disabled={!!noteShareDisabledReason}
            class={`w-23px h-24px rounded-md invisible group-hover:visible ${buttonTheme}`}
          />
        )}

        {this.showMenu && (
          <call-detail-note-menu
            onClick={(e) =>
              // Block propagation to playClip
              e.stopPropagation()
            }
            icon='three-dots-vertical'
            class={`flex flex-shrink-0 justify-center items-center w-19px h-24px text-xl rounded-md cursor-pointer invisible group-hover:visible ${buttonTheme}`}
            clip={this.clip}
            allowShare={false}
            allowChangeHighlightType={false}
            allowDelete={
              !this.readonly && (isAnnotation(this.clip.note.bookmark) || isActionItem(this.clip.note.bookmark))
            }
            allowDownload={isAnnotation(this.clip.note.bookmark) || isScreenShare(this.clip.note.bookmark)}
            allowAddToPlaylist={
              !this.readonly &&
              !!NotesStore.state.playlistActions?.createUrl &&
              !!NotesStore.state.playlistActions?.indexUrl &&
              isHighlight(this.clip.note.bookmark)
            }
            onDelete={(event) => {
              event.stopImmediatePropagation()
              this.delete.emit(this.clip.note)
            }}
          />
        )}
      </Host>
    )
  }
}
